// Auth Context - Global authentication state management

import { createContext, useContext, useEffect, useState, useCallback, useMemo, type ReactNode } from 'react';
import type { AuthState, LoginCredentials, User } from '../types/auth';
import { authService, impersonationService } from '../services/auth';

interface AuthContextType extends AuthState {
  login: (credentials: LoginCredentials) => Promise<User>;
  logout: () => Promise<void>;
  checkAuth: () => Promise<void>;
  hasScope: (scope: string) => boolean;
  hasAnyScope: (...scopes: string[]) => boolean;
  isImpersonating: boolean;
  /**
   * The person actually at the keyboard: the admin while they "View as"
   * someone else, otherwise `user`. Identity-bound tooling (the Guru feedback
   * widget) follows this, not the viewed account.
   */
  realUser: User | null;
  /** Switch to another user (admin View As); resolves to the user now viewed. */
  impersonate: (userId: string) => Promise<User>;
  stopImpersonating: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | null>(null);

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [state, setState] = useState<AuthState>({
    user: null,
    tokens: null,
    isAuthenticated: false,
    isLoading: true,
    error: null,
  });

  const [isImpersonating, setIsImpersonating] = useState(
    impersonationService.isImpersonating(),
  );

  const checkAuth = useCallback(async () => {
    setState((prev) => ({ ...prev, isLoading: true, error: null }));
    try {
      const user = await authService.verifyAuth();
      if (user) {
        setState({
          user,
          tokens: null, // Tokens stored in localStorage, not state
          isAuthenticated: true,
          isLoading: false,
          error: null,
        });
      } else {
        setState({
          user: null,
          tokens: null,
          isAuthenticated: false,
          isLoading: false,
          error: null,
        });
      }
    } catch {
      setState({
        user: null,
        tokens: null,
        isAuthenticated: false,
        isLoading: false,
        error: null,
      });
    }
    setIsImpersonating(impersonationService.isImpersonating());
  }, []);

  const login = useCallback(async (credentials: LoginCredentials): Promise<User> => {
    setState((prev) => ({ ...prev, isLoading: true, error: null }));
    try {
      const { user, tokens } = await authService.login(credentials);
      setState({
        user,
        tokens,
        isAuthenticated: true,
        isLoading: false,
        error: null,
      });
      // A fresh login ends any previous impersonation session.
      setIsImpersonating(false);
      return user;
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Login failed';
      setState((prev) => ({
        ...prev,
        isLoading: false,
        error: message,
      }));
      throw error;
    }
  }, []);

  const logout = useCallback(async () => {
    await authService.logout();
    setIsImpersonating(false);
    setState({
      user: null,
      tokens: null,
      isAuthenticated: false,
      isLoading: false,
      error: null,
    });
  }, []);

  const currentUser = state.user;
  const impersonate = useCallback(async (userId: string): Promise<User> => {
    const { user } = await authService.impersonate(userId, currentUser);
    setState((prev) => ({
      ...prev,
      user,
    }));
    setIsImpersonating(true);
    return user;
  }, [currentUser]);

  const stopImpersonating = useCallback(async () => {
    const adminUser = await authService.stopImpersonating();
    setIsImpersonating(false);
    if (adminUser) {
      setState((prev) => ({
        ...prev,
        user: adminUser,
      }));
      return;
    }
    // The admin tokens are back in place but /auth/me could not confirm the
    // admin (expired refresh token, network blip). Never leave `user` pointing
    // at the impersonated account: the admin-only route guard reads `user`,
    // so a stale non-admin identity would bounce the admin out of the admin
    // panel with no "Return to Admin" control left to click. Re-derive the
    // session from the restored tokens instead — that yields the admin, or an
    // honest signed-out state that ProtectedRoute turns into /login.
    await checkAuth();
  }, [checkAuth]);

  // Check auth on mount
  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const hasScope = useCallback(
    (scope: string): boolean => {
      if (!state.user) return false;
      if (state.user.role === 'ADMIN') return true;
      return state.user.scopes?.includes(scope) ?? false;
    },
    [state.user],
  );

  const hasAnyScope = useCallback(
    (...scopes: string[]): boolean => {
      if (!state.user) return false;
      if (state.user.role === 'ADMIN') return true;
      return scopes.some((s) => state.user?.scopes?.includes(s));
    },
    [state.user],
  );

  const realUser = useMemo(
    () => (isImpersonating ? impersonationService.getStashedAdmin() : currentUser),
    [isImpersonating, currentUser],
  );

  const value: AuthContextType = {
    ...state,
    realUser,
    login,
    logout,
    checkAuth,
    hasScope,
    hasAnyScope,
    isImpersonating,
    impersonate,
    stopImpersonating,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextType {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

/**
 * Non-throwing admin check for SHARED components (e.g. the agent-console trace
 * renderer) that may render in isolation tests outside an AuthProvider. Returns
 * false when there is no provider or no user, so a missing context fails CLOSED
 * (treated as a non-admin) rather than throwing or leaking admin-only detail.
 */
export function useIsAdmin(): boolean {
  const context = useContext(AuthContext);
  return context?.user?.role === 'ADMIN';
}

/**
 * Non-throwing read of the signed-in user for pages that want to short-circuit
 * on an access rule (e.g. an admin-only queue rendering a "you need admin
 * access" panel instead of firing requests that will 403) while still
 * rendering in isolation tests outside an AuthProvider. Returns null when
 * there is no provider or nobody is signed in; callers must then defer to the
 * server's answer rather than assume access.
 */
export function useCurrentUser(): User | null {
  const context = useContext(AuthContext);
  return context?.user ?? null;
}
